import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import Skeleton from "components/Skeleton/Skeleton";
import { Modal } from "@mui/material";
import PropTypes from "prop-types";
import axios from "axios";
import {
  LuX,
  LuFilePlus,
  LuFileText,
  LuSearch,
  LuChevronDown,
  LuCheck,
  LuCircleAlert,
} from "react-icons/lu";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";

function formatDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  return Number.isNaN(d.getTime())
    ? String(val)
    : d.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function getInitials(name) {
  if (!name) return "QU";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function CreateInvoiceModal({
  open,
  onClose,
  onSelectDirect,
  onSelectQuotation,
  businessUserID,
  darkMode = false,
}) {
  const [creationType, setCreationType] = useState("direct");
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [activeQuoteIndex, setActiveQuoteIndex] = useState(-1);
  const [quotesError, setQuotesError] = useState("");
  const quoteSearchRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const backdropRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !dropdownOpen) return;
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
      const below = Math.max(0, viewportBottom - rect.bottom - 14);
      const above = Math.max(0, rect.top - viewportTop - 14);
      const openAbove = below < 240 && above > below;
      setDropdownPosition({
        top: openAbove ? undefined : rect.bottom + 6,
        bottom: openAbove ? window.innerHeight - rect.top + 6 : undefined,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(240, openAbove ? above : below),
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePosition) : null;
    if (triggerRef.current) observer?.observe(triggerRef.current);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [open, dropdownOpen, selectedQuoteId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setDropdownOpen(false);
        setActiveQuoteIndex(-1);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [dropdownOpen]);

  // Fetch quotations when modal opens
  useEffect(() => {
    if (!open) return;
    setCreationType("direct");
    setSelectedQuoteId("");
    setSearchQuery("");
    setDropdownOpen(false);
    setActiveQuoteIndex(-1);
    setQuotes([]);
    setQuotesError("");

    const bUserId =
      businessUserID ||
      (() => {
        try {
          return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}")
            ?.businessUserID;
        } catch {
          return "";
        }
      })();

    if (!bUserId) return;
    let isCancelled = false;
    setLoadingQuotes(true);

    const token = sessionStorage.getItem("TOKEN");
    axios
      .post(
        `${appConstants.baseURL}quotation/fetchQuotations`,
        {
          businessUserID: bUserId,
          quotationStatus: "Approved",
          page: 1,
          limit: 200,
          token,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      )
      .then((res) => {
        if (isCancelled) return;
        const docs =
          res.data?.Result_Output?.docs ||
          (Array.isArray(res.data?.Result_Output) ? res.data?.Result_Output : []);
        // Only show approved quotations still available for invoicing.
        const approvedDocs = docs.filter((q) => {
          if (q.invoiceID || q.createInvoiceAvailable === false) return false;
          const qStatus = String(q.quotationStatus || "").trim().toLowerCase();
          const aStatus = String(q.activityStatus || "").trim().toLowerCase();
          const lStatus = String(q.listStatus || "").trim().toLowerCase();
          return (
            qStatus === "approved" ||
            qStatus === "accepted" ||
            aStatus === "approved" ||
            aStatus === "accepted" ||
            lStatus === "approved" ||
            lStatus === "accepted"
          );
        });
        setQuotes(approvedDocs);
      })
      .catch((err) => {
        if (isCancelled) return;
        setQuotesError("Could not load quotations. Close this dialog and try again.");
        console.error("Error fetching quotes for invoice creation:", err);
      })
      .finally(() => {
        if (!isCancelled) setLoadingQuotes(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [open, businessUserID]);

  // Selected quote object
  const selectedQuote = useMemo(() => {
    return quotes.find((q) => String(q._id) === String(selectedQuoteId));
  }, [quotes, selectedQuoteId]);

  // Filtered quotes based on search query (only approved quotes)
  const filteredQuotes = useMemo(() => {
    const qTrim = selectedQuoteId ? "" : searchQuery.trim().toLowerCase();
    return quotes.filter((q) => {
      const qStatus = String(q.quotationStatus || "").trim().toLowerCase();
      const aStatus = String(q.activityStatus || "").trim().toLowerCase();
      const lStatus = String(q.listStatus || "").trim().toLowerCase();
      const isApproved =
        qStatus === "approved" ||
        qStatus === "accepted" ||
        aStatus === "approved" ||
        aStatus === "accepted" ||
        lStatus === "approved" ||
        lStatus === "accepted";
      if (!isApproved) return false;

      const qNum = String(q.quotationNumber || "").toLowerCase();
      const cName = String(
        q.customerCompanyName || q.customerName || q.name || ""
      ).toLowerCase();
      if (!qTrim) return true;
      return qNum.includes(qTrim) || cName.includes(qTrim);
    });
  }, [quotes, searchQuery, selectedQuoteId]);

  useEffect(() => {
    if (!dropdownOpen || activeQuoteIndex < 0) return;
    const menu = menuRef.current;
    const option = document.getElementById(`invoice-quotation-option-${activeQuoteIndex}`);
    if (!menu || !option) return;
    const itemBounds = option.getBoundingClientRect();
    const menuBounds = menu.getBoundingClientRect();
    if (itemBounds.top < menuBounds.top) menu.scrollTop -= menuBounds.top - itemBounds.top;
    if (itemBounds.bottom > menuBounds.bottom) menu.scrollTop += itemBounds.bottom - menuBounds.bottom;
  }, [activeQuoteIndex, dropdownOpen]);

  const selectQuote = (quote) => {
    const name = quote.customerCompanyName || quote.customerName || quote.name || "Customer";
    const amount = formatCurrencyAUD(parseFloat(quote.finalAmount || quote.totalAmount || 0));
    setSelectedQuoteId(quote._id);
    setSearchQuery(`${quote.quotationNumber || "QUO"} · ${name} · $${amount}`);
    quoteSearchRef.current?.focus({ preventScroll: true });
    setDropdownOpen(false);
    setActiveQuoteIndex(-1);
  };

  const handleSearchKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === "Escape" && dropdownOpen) {
      event.preventDefault();
      event.stopPropagation();
      setDropdownOpen(false);
      setActiveQuoteIndex(-1);
    } else if (event.key === "Tab") {
      setDropdownOpen(false);
      setActiveQuoteIndex(-1);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setDropdownOpen(true);
      if (loadingQuotes || !filteredQuotes.length) return;
      setActiveQuoteIndex((index) => event.key === "ArrowDown"
        ? (index + 1) % filteredQuotes.length
        : (index <= 0 ? filteredQuotes.length : index) - 1);
    } else if (event.key === "Enter" && dropdownOpen) {
      event.preventDefault();
      if (!loadingQuotes && filteredQuotes[activeQuoteIndex]) selectQuote(filteredQuotes[activeQuoteIndex]);
    }
  };

  if (!open) return null;

  const handleContinue = () => {
    if (creationType === "direct") {
      onSelectDirect();
    } else {
      if (!selectedQuoteId || !selectedQuote) return;
      onSelectQuotation(selectedQuoteId, selectedQuote);
    }
  };

  // Calculations for selected quotation preview
  const quoteTotal = selectedQuote
    ? parseFloat(selectedQuote.finalAmount || selectedQuote.totalAmount || 0)
    : 0;
  const quoteSubtotal =
    selectedQuote?.gstType === "No GST"
      ? quoteTotal
      : quoteTotal > 0
      ? quoteTotal / 1.1
      : 0;
  const quoteGst = quoteTotal > 0 ? quoteTotal - quoteSubtotal : 0;

  const quoteItems = selectedQuote?.itemsList || selectedQuote?.items || [];
  const customerName =
    selectedQuote?.customerCompanyName ||
    selectedQuote?.customerName ||
    selectedQuote?.name ||
    "Customer";
  const customerInitials = getInitials(customerName);
  const quoteDateStr = formatDate(
    selectedQuote?.quotationDate || selectedQuote?.createdAt
  );
  const validUntilStr = formatDate(
    selectedQuote?.validUptoDate ||
      selectedQuote?.expiredDate ||
      (selectedQuote?.quotationDate
        ? new Date(new Date(selectedQuote.quotationDate).getTime() + 30 * 86400000)
        : null)
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      hideBackdrop
      className={`invoice-creation-modal-backdrop${darkMode ? " dark" : ""}`}
      style={{ zIndex: 10001 }}
      ref={backdropRef}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-invoice-title"
    >
      <div
        className="invoice-creation-modal"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Figma Header (Frame: Header) */}
        <div className="invoice-creation-modal-header">
          <div className="invoice-creation-modal-header-left">
            <div className="invoice-creation-header-icon" aria-hidden="true">
              <LuFileText />
            </div>
            <h2 id="create-invoice-title">Create invoice</h2>
          </div>
          <button
            type="button"
            className="invoice-creation-modal-close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <LuX />
          </button>
        </div>

        {/* Header Divider */}
        <div className="invoice-creation-divider" />

        {/* Subtitle / Description */}
        <p className="invoice-creation-description">
          Choose whether to start with a blank invoice or pre-fill from an existing quote.
        </p>

        {/* Main Body */}
        <div className="invoice-creation-modal-body">
          {/* Invoice Type Choices */}
          <div className="invoice-creation-options-grid">
            {/* Choice 1: Direct invoice */}
            <div
              id="creation-type-direct"
              data-type="direct"
              className={`invoice-creation-card${
                creationType === "direct" ? " is-selected" : ""
              }`}
              onClick={() => {
                setCreationType("direct");
                setDropdownOpen(false);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCreationType("direct");
                  setDropdownOpen(false);
                }
              }}
            >
              <div className="invoice-creation-card-top">
                <div className="invoice-creation-radio">
                  {creationType === "direct" && <div className="radio-inner" />}
                </div>
                <div className="invoice-creation-card-icon">
                  <LuFilePlus />
                </div>
              </div>
              <div className="invoice-creation-card-content">
                <h3 className="choice-title">Direct invoice</h3>
                <p className="choice-description">
                  Start a new blank invoice with custom customer, dates, and line items.
                </p>
              </div>
            </div>

            {/* Choice 2: From quotation */}
            <div
              id="creation-type-quote"
              data-type="quote"
              className={`invoice-creation-card${
                creationType === "quote" ? " is-selected" : ""
              }`}
              onClick={() => {
                setCreationType("quote");
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCreationType("quote");
                }
              }}
            >
              <div className="invoice-creation-card-top">
                <div className="invoice-creation-radio">
                  {creationType === "quote" && <div className="radio-inner" />}
                </div>
                <div className="invoice-creation-card-icon">
                  <LuFileText />
                </div>
              </div>
              <div className="invoice-creation-card-content">
                <h3 className="choice-title">From quotation</h3>
                <p className="choice-description">
                  Pre-fill customer, pricing, items, and scope notes from an existing quote.
                </p>
              </div>
            </div>
          </div>

          {/* Quotation Selection Section (Only when creationType === "quote") */}
          {creationType === "quote" && (
            <div className="invoice-creation-quote-flow">
              {/* Searchable quotation dropdown */}
              <div
                className="invoice-creation-dropdown-group"
                ref={dropdownRef}
                onBlurCapture={(event) => {
                  if (!dropdownRef.current?.contains(event.relatedTarget) && !menuRef.current?.contains(event.relatedTarget)) {
                    setDropdownOpen(false);
                    setActiveQuoteIndex(-1);
                  }
                }}
              >
                <div className="invoice-creation-label-row">
                  <label htmlFor="quote-search-input" className="invoice-creation-label">
                    Quotation <span className="required-marker">*</span>
                  </label>
                </div>

                <div
                  className={`invoice-creation-trigger-container${
                    dropdownOpen ? " is-active" : ""
                  }`}
                  ref={triggerRef}
                  onClick={() => {
                    quoteSearchRef.current?.focus({ preventScroll: true });
                    setDropdownOpen(true);
                  }}
                >
                  <LuSearch className="trigger-search-icon" />
                  <ClearableSearchInput
                    ref={quoteSearchRef}
                    id="quote-search-input"
                    role="combobox"
                    aria-expanded={dropdownOpen}
                    aria-controls={dropdownOpen ? "invoice-quotation-results" : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={dropdownOpen && activeQuoteIndex >= 0 ? `invoice-quotation-option-${activeQuoteIndex}` : undefined}
                    type="text"
                    className="trigger-search-input"
                    placeholder="Search by quote number or customer"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedQuoteId("");
                      setActiveQuoteIndex(-1);
                      if (!dropdownOpen) setDropdownOpen(true);
                    }}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => {
                      setDropdownOpen(true);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDropdownOpen(true);
                    }}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="invoice-quotation-search-toggle"
                    aria-label={`${dropdownOpen ? "Close" : "Open"} quotation options`}
                    aria-expanded={dropdownOpen}
                    aria-controls={dropdownOpen ? "invoice-quotation-results" : undefined}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      quoteSearchRef.current?.focus({ preventScroll: true });
                      setDropdownOpen(!dropdownOpen);
                      setActiveQuoteIndex(-1);
                    }}
                  >
                    <LuChevronDown className={`trigger-chevron${dropdownOpen ? " open" : ""}`} />
                  </button>

                {/* Floating Dropdown Menu */}
                {dropdownOpen && dropdownPosition && backdropRef.current && createPortal(
                  <div
                    className="invoice-creation-dropdown-menu"
                    ref={menuRef}
                    style={dropdownPosition}
                    role="listbox"
                    id="invoice-quotation-results"
                    aria-label="Available quotations"
                    aria-busy={loadingQuotes}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {loadingQuotes ? (
                      <div
                        className="dropdown-loading-options"
                        role="status"
                        aria-label="Loading quotations"
                      >
                        {[0, 1, 2].map((option) => (
                          <div
                            className="dropdown-menu-item"
                            aria-hidden="true"
                            key={option}
                          >
                            <Skeleton width={`${82 - option * 9}%`} height={14} />
                          </div>
                        ))}
                      </div>
                    ) : quotesError ? (
                      <div className="dropdown-empty-item" role="alert">
                        <LuCircleAlert aria-hidden="true" />
                        <span>{quotesError}</span>
                      </div>
                    ) : filteredQuotes.length === 0 ? (
                      <div className="dropdown-empty-item">
                        <span>
                          {searchQuery
                            ? "No available quotations match your search."
                            : "No quotations available for invoicing."}
                        </span>
                      </div>
                    ) : (
                      filteredQuotes.map((q, index) => {
                        const isSelected = String(q._id) === String(selectedQuoteId);
                        const cCust =
                          q.customerCompanyName ||
                          q.customerName ||
                          q.name ||
                          "Customer";
                        const qAmt = formatCurrencyAUD(
                          parseFloat(q.finalAmount || q.totalAmount || 0)
                        );
                        const qDate = formatDate(q.quotationDate || q.createdAt);

                        return (
                          <div
                            key={q._id}
                            id={`invoice-quotation-option-${index}`}
                            className={`dropdown-menu-item${
                              isSelected ? " is-selected" : ""
                            }${activeQuoteIndex === index ? " is-active" : ""}`}
                            onMouseDown={(event) => event.preventDefault()}
                            onMouseEnter={() => setActiveQuoteIndex(index)}
                            onClick={() => selectQuote(q)}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <span className="dropdown-item-label">
                              {q.quotationNumber || "QUO"} · {cCust} · {qDate} · ${qAmt}
                            </span>
                            {isSelected && <LuCheck className="dropdown-item-check" />}
                          </div>
                        );
                      })
                    )}
                  </div>,
                  backdropRef.current
                )}
                </div>
              </div>

              {/* Selected quotation Preview Card (Figma: Selected quotation preview) */}
              {selectedQuote && (
                <div className="invoice-creation-preview-section">
                  <h4 className="preview-section-title">Selected quotation</h4>

                  <div className="invoice-creation-preview-card">
                    {/* Header: Quote number + Status pill */}
                    <div className="preview-card-header">
                      <span className="preview-quote-number">
                        {selectedQuote.quotationNumber || "QUO"}
                      </span>
                      <div className="preview-badge-accepted">
                        <span className="preview-badge-dot" />
                        <span className="preview-badge-label">
                          {selectedQuote.quotationStatus || "Accepted"}
                        </span>
                      </div>
                    </div>

                    {/* Client & details info */}
                    <div className="preview-client-row">
                      <div className="preview-avatar" aria-hidden="true">
                        <span className="avatar-initials">{customerInitials}</span>
                      </div>
                      <div className="preview-client-info">
                        <strong className="preview-client-name">{customerName}</strong>
                        <p className="preview-dates-line">
                          {quoteDateStr} · Valid until {validUntilStr}
                        </p>

                        {/* Items preview list */}
                        {quoteItems.length > 0 && (
                          <div className="preview-items-list">
                            {quoteItems.slice(0, 3).map((it, idx) => (
                              <div key={idx} className="preview-item-row">
                                {it.itemName || it.itemDescription || "Item"} ·{" "}
                                {it.itemQuantity || 1} × $
                                {formatCurrencyAUD(parseFloat(it.itemPrice || 0))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Subtotal & GST line */}
                        <p className="preview-subtotal-line">
                          Subtotal ${formatCurrencyAUD(quoteSubtotal)} · GST (10%) $
                          {formatCurrencyAUD(quoteGst)}
                        </p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="preview-card-divider" />

                    {/* Card Footer: Total amount */}
                    <div className="preview-card-footer">
                      <strong className="preview-total-amount">
                        ${formatCurrencyAUD(quoteTotal)}
                      </strong>
                      <span className="preview-total-label">
                        Total · AUD incl. GST
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Conversion Rule Note */}
              <p className="invoice-conversion-rule-note">
                This quotation creates one invoice for the full amount. All customer details, items, pricing, and scope notes will be copied.
              </p>
            </div>
          )}
        </div>

        {/* Confirmation Divider */}
        <div className="invoice-creation-divider" />

        {/* Footer (Frame: Footer) */}
        <div className="invoice-creation-modal-footer">
          <button
            type="button"
            className="invoice-creation-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="invoice-creation-btn-submit"
            disabled={creationType === "quote" && !selectedQuoteId}
            onClick={handleContinue}
          >
            {creationType === "quote"
              ? "Create invoice from quote"
              : "Create new invoice"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

CreateInvoiceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectDirect: PropTypes.func.isRequired,
  onSelectQuotation: PropTypes.func.isRequired,
  businessUserID: PropTypes.string,
  darkMode: PropTypes.bool,
};
